import { Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { Stack } from "@mui/system";

import { OpenDrawerMenuButton } from "components/Carrier/components/OpenDrawerMenuButton";
import { ErrorTypography } from "components/ErrorTypography";
import { EvotruxDivider } from "components/EvotruxDivider";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import { RefreshPageButton } from "components/RefreshPageButton";
import { RouteTab } from "components/ShipmentOverviewTab/components/RouteTab";
import { ShipmentStatusBadge } from "components/ShipmentStatusBadge";
import { useDisclosure, useRoleContext, useSwrFetch } from "hooks";
import { useIsMobile } from "hooks/useIsMobile";

import { ExportSupplierShipmentToPDFModal } from "../ExportSupplierShipmentToPDFModal";
import { SupplierShipmentDetailTitle } from "../SupplierShipmentDetailTitle";
import { SupplierShipmentSideBarMenu } from "../SupplierShipmentSideBarMenu";
import {
  SupplierShipmentChip,
  SupplierShipmentDetails,
  SupplierShipmentStatusSection,
  SupplierShipmentTrackingSection,
} from "./components";

interface Props {
  shipmentId: number;
}

const sectionHeaderType = "h5";

export function SupplierShipmentOverviewExpanded(props: Props) {
  const { shipmentId } = props;
  const { role } = useRoleContext();
  const url =
    role?.type === "SHIPPER"
      ? `/shippers/${role.id}/supplier-shipments/${shipmentId}`
      : null;

  const {
    data: shipment,
    loading,
    error,
    refetch,
  } = useSwrFetch<SupplierShipment>({
    url,
    errorMessage: "Failed to fetch supplier shipment details",
  });

  // Sidebar state
  const showFullSidebar = !useIsMobile({ maxWidth: 1200 });
  const { onOpen: onSidebarOpen } = useDisclosure(false);

  // PDF Export state
  const {
    isOpen: pdfExportOpen,
    onOpen: onPdfExportOpen,
    onClose: onPdfExportClose,
  } = useDisclosure(false);

  // Right sidebar state
  const {
    isOpen: rightSidebarOpen,
    onOpen: onRightSidebarOpen,
    onClose: onRightSidebarClose,
  } = useDisclosure(false);

  if (role?.type !== "SHIPPER") {
    return null;
  }

  return (
    <LoadingWrappedNode loading={loading}>
      <ErrorTypography error={error} />

      {shipment && (
        <Stack direction="column" p=".3rem" position="relative">
          <Stack direction="column">
            <SupplierShipmentDetailTitle shipment={shipment} />
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <ShipmentStatusBadge
                  status={shipment?.shipment_status}
                  size="small"
                />
                <SupplierShipmentChip size="small" color="info" />
                <RefreshPageButton
                  id="refresh-supplier-shipment-detail"
                  iconButton
                  onRefresh={refetch}
                  hardReload={false}
                />
              </Stack>
              <IconButton
                onClick={onSidebarOpen}
                sx={{
                  display: showFullSidebar ? "none" : "block",
                }}
              />
              {/* Right Drawer Menu Button */}
              <OpenDrawerMenuButton
                onOpen={onRightSidebarOpen}
                buttonLabel=""
              />
            </Stack>
            <SupplierShipmentStatusSection
              dividerComponent={
                <>
                  <Typography variant={sectionHeaderType} sx={{ mt: "1rem" }}>
                    Shipment Status
                  </Typography>
                  <EvotruxDivider />
                </>
              }
              shipment={shipment}
            />
            <SupplierShipmentTrackingSection
              dividerComponent={
                <>
                  <Typography variant={sectionHeaderType} sx={{ mt: "1rem" }}>
                    Shipment Tracking
                  </Typography>
                  <EvotruxDivider />
                </>
              }
              shipment={shipment}
            />
            <Typography variant={sectionHeaderType} sx={{ mt: "1rem" }}>
              Shipment Details
            </Typography>
            <EvotruxDivider />
            <SupplierShipmentDetails shipment={shipment} />
            <Typography variant={sectionHeaderType} sx={{ mt: "1rem" }}>
              Route
            </Typography>
            <EvotruxDivider />
            <RouteTab shipment={shipment} />
          </Stack>
        </Stack>
      )}

      {/* Right Sidebar Menu */}
      <SupplierShipmentSideBarMenu
        open={rightSidebarOpen}
        handleDrawerClose={onRightSidebarClose}
        onOpenPdfPreview={onPdfExportOpen}
        shipment={shipment ?? undefined}
      />

      {/* PDF Export Modal */}
      {shipment && (
        <ExportSupplierShipmentToPDFModal
          open={pdfExportOpen}
          onClose={onPdfExportClose}
          supplierShipment={shipment}
        />
      )}
    </LoadingWrappedNode>
  );
}
